// Lib
import { mergeWith, sum } from 'lodash';

// Utils
import { getElementType } from './utils/elementPropertyUtils';
import { isBoardLike, isContent } from './utils/elementTypeUtils';

// Types
import { BOARD_TYPE } from '../boards/boardConstants';
import { ALIAS_TYPE } from '../alias/aliasConstants';
import { MNElement } from './elementModelTypes';

export interface ElementCountMap {
    [key: string]: number;
}

export type ReduceTypeChange = string | Partial<MNElement>;

const add = (a = 0, b = 0) => a + b;

export const mergeElementCountMaps = (countMapA: ElementCountMap, countMapB: ElementCountMap): ElementCountMap =>
    mergeWith(countMapA, countMapB, add);

// ----------------------
// Turn an array of [{ elementType: 'BOARD' }, { elementType: 'BOARD' }, { elementType: 'CARD' }, { elementType: 'LINK' }]
// into a map of { BOARD: 2, CARD: 1, LINK: 1 }
// ----------------------
export const reduceTypeCounts = (acc: ElementCountMap, change: ReduceTypeChange): ElementCountMap => {
    const type = getElementType(change);
    const count = acc[type] || 0;
    acc[type] = count + 1;
    return acc;
};

/**
 * This is used for emails, thus changes all types to either 'board' or 'card'.
 */
export const reduceContentCounts = (acc: ElementCountMap, change: ReduceTypeChange): ElementCountMap => {
    if (!isBoardLike(change) && !isContent(change)) return acc;

    const type = isBoardLike(change) ? 'board' : 'card';
    const count = acc[type] || 0;
    acc[type] = count + 1;
    return acc;
};

/**
 * Instead of showing "Aliases" in a count map for render, just treat aliases as boards.
 */
export const convertElementCountMapForRender = (countMap: ElementCountMap): ElementCountMap => {
    if (!countMap) return countMap;

    const returnCountMap = {
        ...countMap,
    };

    delete returnCountMap[ALIAS_TYPE];
    const boardCount = (countMap[BOARD_TYPE] || 0) + (countMap[ALIAS_TYPE] || 0);

    if (boardCount > 0) {
        returnCountMap[BOARD_TYPE] = boardCount;
    }

    return returnCountMap;
};

/**
 * Sums all the counts in a count map.
 */
export const getElementCountMapTotalElementCount = (countMap: ElementCountMap): number => {
    const values = Object.values(countMap);
    return sum(values) || 0;
};

/**
 * Sums the counts for content element types in a count map.
 */
export const getElementCountMapTotalContentCount = (countMap: ElementCountMap): number => {
    let total = 0;
    for (const elementType in countMap) {
        if (!isContent(elementType)) continue;

        total += countMap[elementType] || 0;
    }

    return total;
};
